/**
 * Script-based language detection, mirroring `rag/normalize.py`.
 *
 * The same rule on both sides: count Arabic letters as a share of all letters, threshold at 30%.
 * A majority rule would misclassify an Arabic question naming an English product
 * ("ما هي شروط حساب Premium Business؟"), which is a common way employees actually write.
 *
 * Duplicated in TypeScript rather than fetched from the server because the UI needs it
 * synchronously, per message, while rendering. The threshold is the thing to keep in step; there
 * is a test asserting the shared cases.
 */

export type DetectedLanguage = "ar" | "en";

/** Arabic script blocks, including the presentation forms legacy converters emit. */
const ARABIC = /[؀-ۿݐ-ݿࡰ-ࣿﭐ-﷿ﹰ-﻿]/;

/** Letters in any script. Unicode property escapes need the `u` flag. */
const LETTER = /\p{L}/u;

/** Fraction of letters that must be Arabic for the text to count as Arabic. */
export const ARABIC_THRESHOLD = 0.3;

export function detectLanguage(text: string): DetectedLanguage {
  let arabic = 0;
  let letters = 0;

  for (const char of text) {
    if (!LETTER.test(char)) continue;
    letters += 1;
    if (ARABIC.test(char)) arabic += 1;
  }

  if (letters === 0) return "en";
  return arabic / letters >= ARABIC_THRESHOLD ? "ar" : "en";
}
